import { apiQuery, atsApiQuery } from '..';
import { transformApprovalTasks } from './transformApprovalTasks';

// The dashboard's own reads. They were `builder.mutation`s, which RTK never
// caches: every visit to the dashboard refetched all four behind spinners,
// ~3s on staging. As queries the dashboard shows what it last loaded at once
// and refreshes it in the background (see `readThrough`), and a request
// started at boot (`app/preloadDashboardRoute`) is shared with the page.
// Kept for 10 minutes after the dashboard unmounts, so a return within that
// window paints immediately.
const KEEP_DASHBOARD_DATA_FOR = 600;

export const dashboardNormalApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      // Returns every task count in one call:
      // { scorecard, job_approval, offer_approval, survey,
      //   requisition_approval, requisition_approval_grouped }
      // `requisition_approval_grouped` collapses requisitions raised under one
      // headcount into a single row, which is what the Tasks page lists — the
      // raw `requisition_approval` stays behind the sidebar badge.
      // Optional `{ params: { filter_by } }` narrows `scorecard` and the unified
      // `approval` count to the Tasks page filters; `{}` stays the unfiltered
      // entry the sidebar shares.
      getTasksMetrics: builder.query({
        transformResponse: (response) => response.data,
        providesTags: ['GetTasks'],
        query: (args) => {
          return {
            method: 'get',
            url: `/tasks/metrics`,
            params: args?.params,
          };
        },
      }),
      // These five are reads, and they used to be declared as `builder.mutation`
      // purely because the call sites trigger them imperatively. That cost the
      // app every benefit RTK Query exists for: mutations are never cached,
      // never deduplicated and cannot carry `providesTags`, so switching tabs on
      // the Tasks page refetched all five lists every time.
      //
      // They are queries now, consumed through the generated `useLazyXQuery`
      // hooks so the imperative call shape at the call sites is unchanged.
      // `GetTasks` is an existing tag on this base (src/store/query/index.js),
      // so task-completing mutations can invalidate them.
      getScorecardTasks: builder.query({
        transformResponse: (response) => response,
        // Kept with the dashboard's reads (see KEEP_DASHBOARD_DATA_FOR); a
        // completed task still drops it through the `GetTasks` tag.
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/tasks/scorecards`,
            params,
          };
        },
      }),
      getJobApprovalTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { type: 'job', ...params };
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params: newParams,
          };
        },
      }),
      getOfferApprovalTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { type: 'offer', ...params };
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params: newParams,
          };
        },
      }),
      // Every approval type in one server-paginated list (no `type` param);
      // headcount-grouped requisitions arrive already collapsed to one row.
      getApprovalTasks: builder.query({
        transformResponse: transformApprovalTasks,
        // Kept with the dashboard's reads (see KEEP_DASHBOARD_DATA_FOR); a
        // completed task still drops it through the `GetTasks` tag.
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params,
          };
        },
      }),
      getSurveyTasks: builder.query({
        transformResponse: (response) => response,
        // Kept with the dashboard's reads (see KEEP_DASHBOARD_DATA_FOR); a
        // completed task still drops it through the `GetTasks` tag.
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { ...params };
          return {
            method: 'get',
            url: `/tasks/surveys`,
            params: newParams,
          };
        },
      }),
      getRequisitionApprovalTasks: builder.query({
        transformResponse: (response) => response,
        providesTags: ['GetTasks'],
        query: ({ params }) => {
          const newParams = { type: 'requisition', ...params };
          return {
            method: 'get',
            url: `/tasks/approvals`,
            params: newParams,
          };
        },
      }),
      getSurveyFormTemplate: builder.mutation({
        transformResponse: (response) => response,
        query: ({ surveyId }) => {
          return {
            method: 'get',
            url: `/api_ats/v1/survey_form_templates/${surveyId}`,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const dashboardApi = atsApiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getApplicationMetrics: builder.query({
        transformResponse: (response) => response,
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_applications/metrics`,
            params,
          };
        },
      }),

      getDashboardActivitiesMetrics: builder.query({
        transformResponse: (response) => response,
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_applications/dashboard/metrics`,
            params,
          };
        },
      }),
      getDashboardActivities: builder.query({
        transformResponse: (response) => response,
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_applications/dashboard`,
            params,
          };
        },
      }),
      // "Upcoming" is measured from the moment the request goes out, so the
      // time is added here rather than passed in: an argument holding the
      // current time would be a new cache entry on every call.
      getUpcomingInterviews: builder.query({
        transformResponse: (response) => response?.data,
        keepUnusedDataFor: KEEP_DASHBOARD_DATA_FOR,
        query: ({ params }) => {
          return {
            method: 'get',
            url: `/ats_interviews`,
            params: {
              ...params,
              filter_by: { ...params?.filter_by, starts_after: new Date() },
            },
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useLazyGetApplicationMetricsQuery,
  useLazyGetDashboardActivitiesMetricsQuery,
  useLazyGetDashboardActivitiesQuery,
  useLazyGetUpcomingInterviewsQuery,
} = dashboardApi;

export const {
  useGetTasksMetricsQuery,
  useLazyGetTasksMetricsQuery,
  // Lazy variants: the call sites fetch imperatively, but these are cached,
  // deduplicated query entries rather than fire-and-forget mutations.
  useLazyGetScorecardTasksQuery,
  useLazyGetJobApprovalTasksQuery,
  useLazyGetOfferApprovalTasksQuery,
  useLazyGetApprovalTasksQuery,
  useLazyGetSurveyTasksQuery,
  useLazyGetRequisitionApprovalTasksQuery,
  useGetSurveyFormTemplateMutation,
} = dashboardNormalApi;
